import { arrayOf, bool, func, object, number, oneOfType, shape, string } from 'prop-types';
import React, { useCallback } from 'react';
import classNames from 'classnames/bind';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useClientOnlyLayoutEffect } from 'hooks/use-client-only-layout-effect';
import { formatGroceriesMenuNavigation, categoryNameToUrl } from 'utils/format';
import { formatMenuClickEvent } from 'utils/tracking';
import { refType } from 'constants/types/ref';
import SubCategory from 'components/MegaMenu/MegaMenuLink/SubCategory';
import MegaMenuLink from 'components/MegaMenu/MegaMenuLink';
import MenuFocusProvider from 'components/MegaMenu/MenuFocusProvider';
import { useMenuExperienceFragments } from 'contexts/MenuExperienceFragments';
import styles from './MenuLinksList.module.scss';

const MenuLinksList = ({
  activeChildId,
  category,
  keyboardLevel,
  level,
  menuMaxLength,
  menuRoot,
  menus,
  navigateFromMegaMenu,
  navigationRef,
  offersPath,
  parentId,
  path,
  relationship,
  setKeyboardLevel,
  setMegaMenu,
  title,
  banners,
}) => {
  const { trackEvent } = useAnalytics();
  const getItemInfo = useCallback(
    id => {
      const { hasDescendants, name, banner } = category.categories?.[id] ?? {};
      const isParent = hasDescendants && level + 1 < menuMaxLength;

      return { isParent, name, banner };
    },
    [category, level, menuMaxLength],
  );
  const { fetchAndStoreFragment } = useMenuExperienceFragments();

  const handleKeyboardLeft = useCallback(() => {
    if (level) {
      const previousLevel = level - 1;
      // close and fall back to previous level
      setMegaMenu(previousLevel, parentId);

      // ensure keyboard focus side-effects occur
      setKeyboardLevel(previousLevel);
    }
  }, [parentId, level, setKeyboardLevel, setMegaMenu]);

  const handleKeyboardRight = useCallback(
    id => {
      const { isParent, name, banner } = getItemInfo(id);
      const nextLevel = level + 1;

      if (!isParent) return;

      // prevent further navigation during transitions to avoid circular effects
      navigationRef.current.unfocus();

      // open next level
      setMegaMenu(nextLevel, id, name);

      if (banner && !banners[`/ecom${path}/${categoryNameToUrl(name)}`.toLowerCase()]) {
        fetchAndStoreFragment(banner);
      }

      // ensure keyboard focus side-effects occur
      setKeyboardLevel(nextLevel);
    },
    [
      getItemInfo,
      level,
      navigationRef,
      setKeyboardLevel,
      setMegaMenu,
      fetchAndStoreFragment,
      path,
      banners,
    ],
  );

  const handleClickMenuItem = useCallback(
    (event, id, viewAllName = undefined) => {
      const { isParent, name, banner } = getItemInfo(id);

      // Prevent focus side-effects intended for keyboard navigation
      setKeyboardLevel(null);

      if (isParent) {
        // prevent link navigation and open next menu level
        event.preventDefault();
        setMegaMenu(level + 1, id, name);
        if (banner) {
          fetchAndStoreFragment(banner);
        }
      } else {
        const navigation =
          level > 0
            ? `${formatGroceriesMenuNavigation(menus.flatMap(item => item.name))}|G${level + 1}:${
                event.target.title
              }`
            : `${event.target.title}`;

        trackEvent(formatMenuClickEvent(navigation));
        // allow browser to follow link, just close menu
        const vAllName = name || viewAllName;
        navigateFromMegaMenu(level + 1, vAllName, id);
      }
    },
    [
      getItemInfo,
      level,
      menus,
      navigateFromMegaMenu,
      setKeyboardLevel,
      setMegaMenu,
      trackEvent,
      fetchAndStoreFragment,
    ],
  );

  useClientOnlyLayoutEffect(() => {
    // refocus the currently active level, for left/right keyboard navigation

    if (keyboardLevel === level) {
      navigationRef.current.refocus(0);
    }
  }, [keyboardLevel, level, navigationRef]);

  if (!title) return null;

  const componentClass = classNames(styles[relationship], styles[`level${level}`], {
    [styles.active]: relationship === 'ancestor' || level > 0,
  });
  const menuLinks = category.categoryIds;
  const offerLevels = 2;
  const offerStartLevel = menuRoot === 'Shop' ? 1 : 0;
  const showOffers = level >= offerStartLevel && level < offerStartLevel + offerLevels;
  const offersLink = (
    <li className={styles.offers} role="menuitem">
      <MegaMenuLink
        label={`${title} <strong>OFFERS</strong>`}
        path={path}
        shortName="offers"
        title={`${title} Offers`}
        url={offersPath}
        onClick={(event, id) => handleClickMenuItem(event, id, `${title} Offers`)}
      />
    </li>
  );

  return (
    <MenuFocusProvider
      onEnterKey={handleKeyboardRight}
      onLeftKey={handleKeyboardLeft}
      onRightKey={handleKeyboardRight}
      ref={navigationRef}
    >
      <div
        aria-expanded={relationship !== 'descendant'}
        aria-hidden={relationship === 'descendant'}
        className={componentClass}
        data-testid="menu-links-list"
      >
        <ul
          data-test={`mega-menu-${level}-list`}
          className={styles.menuLinks}
          aria-label={`Use up, down, left and right arrows to browse ${title}.`}
          role="menu"
        >
          {/* View all ... link */}
          <li className={styles.all} role="menuitem">
            <MegaMenuLink
              title={`View all ${title}`}
              path={path}
              shortName="all"
              url={path}
              onClick={(event, id) => handleClickMenuItem(event, id, `View all ${title}`)}
            />
          </li>
          {showOffers && offersLink}
          {/* Taxonomy menu items */}
          {menuLinks.map(id => (
            <li key={`${id}`} role="menuitem">
              <SubCategory
                categoryId={id}
                isActive={activeChildId === id}
                level={level}
                onClick={handleClickMenuItem}
                path={path}
              />
            </li>
          ))}
        </ul>
      </div>
    </MenuFocusProvider>
  );
};

MenuLinksList.displayName = 'MenuLinksList';

MenuLinksList.propTypes = {
  activeChildId: string,
  category: shape({
    categoryIds: arrayOf(string),
    categories: shape({
      id: string,
      name: string,
      categoryIds: arrayOf(string),
      hasDescendants: bool,
    }),
  }),
  keyboardLevel: number,
  level: number.isRequired,
  menuMaxLength: number.isRequired,
  menuRoot: string,
  menus: arrayOf(
    shape({
      id: string,
    }),
  ),
  navigateFromMegaMenu: func.isRequired,
  navigationRef: refType.isRequired,
  offersPath: oneOfType([object, string]),
  parentId: string,
  path: string,
  relationship: string,
  setKeyboardLevel: func.isRequired,
  setMegaMenu: func.isRequired,
  title: string,
  banners: shape().isRequired,
};

MenuLinksList.defaultProps = {
  activeChildId: undefined,
  category: {},
  keyboardLevel: null,
  menuRoot: undefined,
  menus: [],
  offersPath: undefined,
  parentId: undefined,
  path: undefined,
  relationship: undefined,
  title: undefined,
};

export default MenuLinksList;
